import React, { useState, useEffect } from 'react';
import {
    Box, Button, Typography, TextField,
    Dialog, DialogTitle, DialogContent, DialogActions,
    Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
    IconButton, Chip, Alert, CircularProgress, Paper,
    Grid, MenuItem, Select, FormControlLabel, Switch
} from '@mui/material';
import {
    Add as AddIcon,
    Edit as EditIcon,
    Delete as DeleteIcon,
    Refresh as RefreshIcon,
    Router as RouterIcon
} from '@mui/icons-material';
import {
    getPools,
    createPool,
    updatePool,
    deletePool,
    getMikrotiks
} from '../api/mikrotik.api';
import { getOLTs } from '../api/olt.api';  // Cambiado de getOlts a getOLTs

const MikroTikPools = () => {
    const [pools, setPools] = useState([]);
    const [mikrotiks, setMikrotiks] = useState([]);
    const [olts, setOlts] = useState([]);
    const [selectedMikrotik, setSelectedMikrotik] = useState('');
    const [loading, setLoading] = useState(false);
    const [openDialog, setOpenDialog] = useState(false);
    const [editingPool, setEditingPool] = useState(null);
    const [formData, setFormData] = useState({
        nombre: '',
        mikrotik_id: '',
        olt_id: '',
        network: '',
        mascara: 24,
        gateway: '',
        description: '',
        enabled: true
    });
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');

    useEffect(() => {
        loadMikrotiks();
        loadOlts();
    }, []);

    useEffect(() => {
        if (selectedMikrotik) {
            loadPools();
        }
    }, [selectedMikrotik]);

    const loadMikrotiks = async () => {
        try {
            const response = await getMikrotiks({ enabled_only: true });
            setMikrotiks(response.data);
            if (response.data.length > 0) {
                setSelectedMikrotik(response.data[0].id);
            }
        } catch (error) {
            setError('Error cargando MikroTiks');
        }
    };

    const loadOlts = async () => {
        try {
            const response = await getOLTs();  // Cambiado de getOlts a getOLTs
            setOlts(Array.isArray(response) ? response : response.data || []);
        } catch (error) {
            console.error('Error cargando OLTs:', error);
        }
    };

    const loadPools = async () => {
        if (!selectedMikrotik) return;
        setLoading(true);
        try {
            const response = await getPools({ mikrotik_id: selectedMikrotik });
            setPools(response.data);
        } catch (error) {
            setError('Error cargando pools');
        }
        setLoading(false);
    };

    const handleOpenDialog = (pool = null) => {
        if (pool) {
            setEditingPool(pool);
            setFormData({
                nombre: pool.nombre,
                mikrotik_id: pool.mikrotik_id,
                olt_id: pool.olt_id || '',
                network: pool.network,
                mascara: pool.mascara,
                gateway: pool.gateway || '',
                description: pool.description || '',
                enabled: pool.enabled
            });
        } else {
            setEditingPool(null);
            setFormData({
                nombre: '',
                mikrotik_id: selectedMikrotik,
                olt_id: '',
                network: '',
                mascara: 24,
                gateway: '',
                description: '',
                enabled: true
            });
        }
        setOpenDialog(true);
    };

    const handleSubmit = async () => {
        if (!formData.nombre) {
            setError('Ingrese un nombre');
            return;
        }
        if (!formData.network) {
            setError('Ingrese la red');
            return;
        }

        try {
            if (editingPool) {
                await updatePool(editingPool.id, formData);
                setSuccess('Pool actualizado exitosamente');
            } else {
                await createPool(formData);
                setSuccess('Pool creado exitosamente');
            }
            handleCloseDialog();
            loadPools();
        } catch (error) {
            setError(error.response?.data?.detail || 'Error al guardar pool');
        }
    };

    const handleDelete = async (id, nombre) => {
        if (!window.confirm(`¿Eliminar pool "${nombre}"?`)) return;
        try {
            await deletePool(id);
            setSuccess('Pool eliminado exitosamente');
            loadPools();
        } catch (error) {
            setError(error.response?.data?.detail || 'Error al eliminar pool');
        }
    };

    const handleCloseDialog = () => {
        setOpenDialog(false);
        setEditingPool(null);
    };

    const getOltNombre = (id) => {
        const olt = olts.find(o => o.id === id);
        return olt ? olt.name : '-';
    };

    return (
        <Box sx={{ p: 3 }}>
            {error && <Alert severity="error" onClose={() => setError('')} sx={{ mb: 2 }}>{error}</Alert>}
            {success && <Alert severity="success" onClose={() => setSuccess('')} sx={{ mb: 2 }}>{success}</Alert>}

            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                <Typography variant="h5">Pools IP</Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                    <Select
                        value={selectedMikrotik}
                        onChange={(e) => setSelectedMikrotik(e.target.value)}
                        size="small"
                        sx={{ minWidth: 200 }}
                    >
                        {mikrotiks.map(m => (
                            <MenuItem key={m.id} value={m.id}>{m.nombre}</MenuItem>
                        ))}
                    </Select>
                    <Button
                        variant="outlined"
                        startIcon={<RefreshIcon />}
                        onClick={loadPools}
                    >
                        Refrescar
                    </Button>
                    <Button
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleOpenDialog()}
                        disabled={!selectedMikrotik}
                    >
                        Nuevo Pool
                    </Button>
                </Box>
            </Box>

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', p: 4 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <TableContainer component={Paper}>
                    <Table>
                        <TableHead>
                            <TableRow>
                                <TableCell>Nombre</TableCell>
                                <TableCell>Red</TableCell>
                                <TableCell>Gateway</TableCell>
                                <TableCell>OLT Asociada</TableCell>
                                <TableCell>Estado</TableCell>
                                <TableCell align="center">Acciones</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {pools.map((pool) => (
                                <TableRow key={pool.id}>
                                    <TableCell>
                                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                                            <RouterIcon sx={{ mr: 1, color: 'secondary.main' }} />
                                            {pool.nombre}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{pool.network}/{pool.mascara}</TableCell>
                                    <TableCell>{pool.gateway || '-'}</TableCell>
                                    <TableCell>{getOltNombre(pool.olt_id)}</TableCell>
                                    <TableCell>
                                        <Chip
                                            label={pool.enabled ? 'Habilitado' : 'Deshabilitado'}
                                            color={pool.enabled ? 'success' : 'default'}
                                            size="small"
                                        />
                                    </TableCell>
                                    <TableCell align="center">
                                        <IconButton size="small" onClick={() => handleOpenDialog(pool)}>
                                            <EditIcon />
                                        </IconButton>
                                        <IconButton size="small" color="error" onClick={() => handleDelete(pool.id, pool.nombre)}>
                                            <DeleteIcon />
                                        </IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                            {pools.length === 0 && (
                                <TableRow>
                                    <TableCell colSpan={6} align="center">
                                        <Typography color="textSecondary">No hay pools configurados para este MikroTik</Typography>
                                    </TableCell>
                                </TableRow>
                            )}
                        </TableBody>
                    </Table>
                </TableContainer>
            )}

            {/* Dialog para crear/editar Pool */}
            <Dialog open={openDialog} onClose={handleCloseDialog} maxWidth="md" fullWidth>
                <DialogTitle>
                    {editingPool ? 'Editar Pool' : 'Nuevo Pool'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 1 }}>
                        <Grid item xs={12}>
                            <TextField
                                label="Nombre"
                                fullWidth
                                value={formData.nombre}
                                onChange={(e) => setFormData({ ...formData, nombre: e.target.value })}
                                required
                            />
                        </Grid>
                        <Grid item xs={8}>
                            <TextField
                                label="Red"
                                fullWidth
                                value={formData.network}
                                onChange={(e) => setFormData({ ...formData, network: e.target.value })}
                                placeholder="Ej: 172.16.200.0"
                                required
                            />
                        </Grid>
                        <Grid item xs={4}>
                            <TextField
                                label="Máscara"
                                type="number"
                                fullWidth
                                value={formData.mascara}
                                onChange={(e) => setFormData({ ...formData, mascara: parseInt(e.target.value) })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Gateway"
                                fullWidth
                                value={formData.gateway}
                                onChange={(e) => setFormData({ ...formData, gateway: e.target.value })}
                                placeholder="Ej: 172.16.200.1"
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <Select
                                fullWidth
                                value={formData.olt_id}
                                onChange={(e) => setFormData({ ...formData, olt_id: e.target.value })}
                                displayEmpty
                            >
                                <MenuItem value="">Sin OLT asociada</MenuItem>
                                {olts.map(olt => (
                                    <MenuItem key={olt.id} value={olt.id}>{olt.name}</MenuItem>
                                ))}
                            </Select>
                        </Grid>
                        <Grid item xs={12}>
                            <TextField
                                label="Descripción"
                                fullWidth
                                multiline
                                rows={2}
                                value={formData.description}
                                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                            />
                        </Grid>
                        <Grid item xs={12}>
                            <FormControlLabel
                                control={
                                    <Switch
                                        checked={formData.enabled}
                                        onChange={(e) => setFormData({ ...formData, enabled: e.target.checked })}
                                    />
                                }
                                label="Habilitado"
                            />
                        </Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialog}>Cancelar</Button>
                    <Button onClick={handleSubmit} variant="contained">
                        {editingPool ? 'Actualizar' : 'Crear'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
};

export default MikroTikPools;
